import type {CreateExtArgs, DmApis, Extension, ExtensionAPI, CreateExtension} from '@wix/document-manager-core'
import type {
    AppController,
    AppControllerData,
    AppControllerDataRef,
    AppControllerRef,
    AppDefinitionId,
    CompRef,
    Pointer
} from '@wix/document-services-types'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {DATA_TYPES} from '../constants/constants'
import {stripHashIfExists} from '../utils/refArrayUtils'
import type {StructureExtensionAPI} from './structure'
import type {ConnectionsAPI} from './connections'
import type {PlatformExtensionAPI} from './platform'
import {getControllerAppDefinitionId} from './appControllerData/appControllerUtils'
import {ReportableError} from '@wix/document-manager-utils'
import {addPlatformAppToPageIfFirstController} from './components/componentHooks/utils/platformAppComp'

export const CONTROLLER_DATA_TYPE = 'AppController'

const CONTROLLER_VALIDATION_ERRORS = {
    MISSING_ID: 'Controller id is required',
    controllerNotFound: (id: string) => `Controller not found for controllerId: ${id}`,
    applicationIdMissmatch: (appId: string) => `Controller not found for applicationId: ${appId}`
} as const

interface ControllersAPI extends ExtensionAPI {
    create(controllerId: string, compPointer: CompRef, controllerData: AppControllerData): Pointer
    remove(compPointer: CompRef, controllerPointer: Pointer): void
    getDefinedControllersIds(compPointer: CompRef): string[]
    getAvailableScopedControllerRefs(componentRef: CompRef): AppControllerDataRef[]
    getAppControllerRef(appController: AppController): AppControllerRef
    getControllerToAddRef(pageId: string, id?: string): Pointer
    getControllerDataPointersByAppDefId(appDefId: string): Pointer[]
}

interface ControllersExtensionAPI extends ExtensionAPI {
    controllers: ControllersAPI
}

const createExtension: CreateExtension = (): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): ControllersExtensionAPI => {
        const {extensionAPI, dal, pointers} = createExtArgs
        const {dataModel} = extensionAPI as DataModelExtensionAPI

        const createControllerList = (compPointer: CompRef): Pointer => {
            const controllerListDataItem = dal.schema.createItemAccordingToSchema(
                'ControllerList',
                DATA_TYPES.controllers,
                {
                    controllers: []
                }
            )
            return dataModel.components.addItem(compPointer, DATA_TYPES.controllers, controllerListDataItem)
        }

        const addControllerToControllerList = (compPointer: CompRef, controllerPointer: Pointer) => {
            let controllerListPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.controllers)
            if (!controllerListPointer) {
                controllerListPointer = createControllerList(compPointer)
            }
            const controllersList = dal.get(controllerListPointer)
            const newControllerList = {
                ...controllersList,
                controllers: [...controllersList.controllers, `#${controllerPointer.id}`]
            }
            dal.set(controllerListPointer, newControllerList)
        }

        const removeControllerFromControllerList = (compPointer: CompRef, controllerPointer: Pointer) => {
            const controllerList = dataModel.components.getItem(compPointer, DATA_TYPES.controllers)
            const remainingControllers = controllerList.controllers.filter(
                ({id}: AppController) => id !== controllerPointer.id
            )

            if (remainingControllers.length > 0) {
                const newControllerList = {
                    ...controllerList,
                    controllers: remainingControllers
                }
                dataModel.components.addItem(compPointer, DATA_TYPES.controllers, newControllerList)
            } else {
                dataModel.components.removeItem(compPointer, DATA_TYPES.controllers)
            }
        }

        const createController = (controllerId: string, controllerData: AppControllerData, pageId: string) => {
            const controllerDataItem = dal.schema.createItemAccordingToSchema(
                CONTROLLER_DATA_TYPE,
                DATA_TYPES.data,
                controllerData
            )
            return dataModel.addItem(controllerDataItem, DATA_TYPES.data, pageId, controllerId)
        }

        const getDefinedControllersIds = (compPointer: Pointer): string[] => {
            const controllerListPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.controllers)

            if (controllerListPointer) {
                const controllerList = dal.get(controllerListPointer)
                if (controllerList?.controllers) {
                    return controllerList.controllers.map(stripHashIfExists) as string[]
                }
            }
            return []
        }

        const create = (controllerId: string, compPointer: CompRef, controllerData: AppControllerData): Pointer => {
            if (!dal.has(compPointer)) {
                throw new Error('Component does not exist')
            }
            const pageId = pointers.structure.getPageOfComponent(compPointer).id
            const controllerPointer = createController(controllerId, controllerData, pageId)
            addControllerToControllerList(compPointer, controllerPointer)
            addPlatformAppToPageIfFirstController(createExtArgs, compPointer, controllerData.applicationId)
            return controllerPointer
        }

        const removePlatformApplicationFromPageIfLastController = (
            compPointer: CompRef,
            controllerDataPointer: Pointer
        ): void => {
            const {platform} = extensionAPI as PlatformExtensionAPI
            const applicationId = getControllerAppDefinitionId(dal.get(controllerDataPointer))

            if (!applicationId) {
                throw new Error('Controller applicationId is missing')
            }

            const controllerPageRef = pointers.structure.getPageOfComponent(compPointer)

            const appControllersInPage = pointers.data.getDataItemsWithPredicate({applicationId}, controllerPageRef.id)
            platform.pagesPlatformApplication.updatePagePlatformApp(
                controllerPageRef,
                applicationId,
                appControllersInPage.length > 1
            )
        }

        const removeControllerConnections = (controllerPointer: Pointer) => {
            const {connections} = extensionAPI as ConnectionsAPI

            const connectedComponents: CompRef[] = connections.getConnectedComponentsForController(controllerPointer)

            connectedComponents.forEach(comp => {
                connections.disconnect(comp, controllerPointer)
            })
        }

        const validateControllerOwnership = (compPointer: CompRef, controllerPointer: Pointer): void => {
            const controllerListPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.controllers)
            if (!controllerListPointer) {
                throw new Error('Controller list does not exist')
            }
            const controllersList = dal.get(controllerListPointer)
            if (!controllersList) {
                throw new Error('Controller list does not exist')
            }
            if (!controllersList.controllers.includes(`#${controllerPointer.id}`)) {
                throw new Error(
                    `Controller is not owned by the component. Component: ${compPointer.id}, Controller: ${controllerPointer.id}`
                )
            }
        }

        const remove = (compPointer: CompRef, controllerPointer: Pointer): void => {
            if (!dal.has(compPointer)) {
                throw new Error(`Component does not exist: ${compPointer.id}`)
            }
            if (!dal.has(controllerPointer)) {
                throw new Error(`Controller does not exist: ${controllerPointer.id}`)
            }
            validateControllerOwnership(compPointer, controllerPointer)

            removeControllerConnections(controllerPointer)
            removePlatformApplicationFromPageIfLastController(compPointer, controllerPointer)

            removeControllerFromControllerList(compPointer, controllerPointer)
            dataModel.removeItemRecursively(controllerPointer)
        }

        const getControllerToAddRef = (pageId: string, id?: string) => {
            const customId = id || dataModel.generateNewId(DATA_TYPES.data)
            return pointers.data.getDataItem(customId, pageId)
        }

        const getAvailableScopedControllerRefs = (compPointer: CompRef): AppControllerDataRef[] => {
            const {components} = extensionAPI as StructureExtensionAPI
            const pageId = pointers.structure.getPageOfComponent(compPointer).id
            const ancestors = [compPointer, ...components.getAncestors(compPointer, true)]

            const allControllerIds = new Set<string>()
            ancestors.forEach((ancestor: Pointer) => {
                const definedControllersIds = getDefinedControllersIds(ancestor)
                definedControllersIds.forEach(controllerId => allControllerIds.add(controllerId))
            })

            return Array.from(allControllerIds).map(
                (controllerId: string) =>
                    pointers.getPointer(controllerId, DATA_TYPES.data, {pageId}) as AppControllerDataRef
            )
        }

        const getAppControllerRef = (appController: AppController): AppControllerRef => {
            if (!appController.id) {
                throw new ReportableError({
                    errorType: 'missingControllerId',
                    message: CONTROLLER_VALIDATION_ERRORS.MISSING_ID
                })
            }

            const controllerPointer = pointers.data.getDataItem(appController.id)

            if (!dal.has(controllerPointer)) {
                throw new ReportableError({
                    errorType: 'controllerNotFound',
                    message: CONTROLLER_VALIDATION_ERRORS.controllerNotFound(appController.id)
                })
            }

            const controllerData = dal.get(controllerPointer)
            if (controllerData?.applicationId !== appController.applicationId) {
                throw new ReportableError({
                    errorType: 'applicationIdMissmatch',
                    message: CONTROLLER_VALIDATION_ERRORS.applicationIdMissmatch(appController.applicationId)
                })
            }

            return controllerPointer as AppControllerRef
        }

        const getControllerDataPointersByAppDefId = (appDefId: AppDefinitionId): Pointer[] => {
            const allControllerPointers = dal.getAllItemsInNamespace(DATA_TYPES.data).filter((pointer: Pointer) => {
                const controllerData = dal.get(pointer)
                return controllerData?.type === CONTROLLER_DATA_TYPE && controllerData?.applicationId === appDefId
            })
            return allControllerPointers
        }

        return {
            controllers: {
                create,
                remove,
                getDefinedControllersIds,
                getControllerToAddRef,
                getAvailableScopedControllerRefs,
                getAppControllerRef,
                getControllerDataPointersByAppDefId
            }
        }
    }

    const createPublicAPI = ({extensionAPI, pointers}: DmApis) => {
        const {controllers, dataModel} = extensionAPI as ControllersExtensionAPI & DataModelExtensionAPI
        return {
            controllers: {
                create(controllerPointer: Pointer, compPointer: CompRef, controllerData: AppController): Pointer {
                    return controllers.create(controllerPointer.id, compPointer, controllerData)
                },
                remove(compPointer: CompRef, controllerPointer: Pointer): void {
                    return controllers.remove(compPointer, controllerPointer)
                },
                getControllerToAddRef(compPointer: CompRef, controllerData: AppController, id?: string): Pointer {
                    return controllers.getControllerToAddRef(pointers.structure.getPageOfComponent(compPointer).id, id)
                },
                getAvailableScopedControllers: (compPointer: CompRef): AppController[] => {
                    return controllers
                        .getAvailableScopedControllerRefs(compPointer)
                        .map((controllerRef: Pointer) =>
                            dataModel.getItem(controllerRef.id, controllerRef.type, controllerRef.pageId)
                        )
                },
                getAppControllerRef: controllers.getAppControllerRef,
                getControllerDataPointersByAppDefId: controllers.getControllerDataPointersByAppDefId
            }
        }
    }

    return {
        name: 'controllers',
        createExtensionAPI,
        createPublicAPI,
        dependencies: new Set(['dataModel', 'structure'])
    }
}

export {createExtension, ControllersExtensionAPI, ControllersAPI}
